import { loadScript } from 'widgets/toolbox/util';
import type { TWidget } from 'widgets/Widget';

type TTamaraWidgetConfig = {
    lang: string;
    country: string;
    publicKey: string;
};

declare global {
    interface Window {
        tamaraWidgetConfig?: TTamaraWidgetConfig;
        TamaraWidgetV2?: { refresh: () => void };
    }
}

/**
 * @description Tamara widget implementation
 * @param Widget Base widget for extending
 * @returns Tamara widget
 */
function TamaraWidgetClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product
     * @class TamaraWidget
     * @augments Widget
     * @classdesc Loads the Tamara widget script (https://docs.tamara.co/docs/direct-widgets), which renders the
     * `tamara-widget` element, and re-renders it once the product amount changes
     * @property {string} data-sdk-url - Tamara widget script URL of the current environment
     * @property {string} data-public-key - Tamara public key of the current environment
     * @property {string} data-country - ISO country code
     * @property {string} data-lang - Widget language, `ar` or `en`
     * @example <caption>Tamara product page widget</caption>
     * <div
     *     data-widget="tamaraWidget"
     *     data-id="tamaraWidget"
     *     data-sdk-url="${tamaraWidgetConfig.sdkUrl}"
     *     data-public-key="${tamaraWidgetConfig.publicKey}"
     *     data-country="${tamaraWidgetConfig.country}"
     *     data-lang="${tamaraWidgetConfig.lang}"
     * >
     *     <tamara-widget type="tamara-summary" inline-type="2" amount="${tamaraWidgetAmount}" data-ref="widget"></tamara-widget>
     * </div>
     */
    class TamaraWidget extends Widget {
        /**
         * @description Set once the widget script fails to load, keeps the widget hidden on variation change
         */
        isScriptFailed = false;

        prefs() {
            return {
                sdkUrl: '',
                publicKey: '',
                country: '',
                lang: '',
                ...super.prefs()
            };
        }

        init(): void {
            super.init();

            const { sdkUrl, publicKey, country, lang } = this.prefs();

            // the script reads its configuration from the global scope once loaded
            window.tamaraWidgetConfig = { lang, country, publicKey };

            if (window.TamaraWidgetV2) {
                window.TamaraWidgetV2.refresh();

                return;
            }

            loadScript(sdkUrl).catch(() => {
                this.isScriptFailed = true;
                this.hide();
            });
        }

        /**
         * @description Re-renders the widget for the amount of the selected variation
         * @param amount Amount with the currency decimals, e.g. `DDDD.dd`, hides the widget when empty
         */
        updateAmount(amount?: string | null): void {
            if (!amount || this.isScriptFailed) {
                this.hide();

                return;
            }

            const widget = this.ref('widget');

            if (widget.attr('amount') !== amount) {
                widget.attr('amount', amount);
                window.TamaraWidgetV2?.refresh();
            }

            this.show();
        }
    }

    return TamaraWidget;
}

export type TTamaraWidget = ReturnType<typeof TamaraWidgetClassCreator>;

export type TTamaraWidgetInstance = InstanceType<TTamaraWidget>;

export default TamaraWidgetClassCreator;
